/**
 * Debug Request Interceptor
 * 
 * This module provides early interception of fetch and XMLHttpRequest calls
 * to capture all network requests, including those made during initial page load.
 * 
 * It's designed to be imported and executed early in the application bootstrap process,
 * and provides an API for the DebugWidget component to access the captured request data.
 */

// Define types for request tracking
interface RequestData {
  id: string;
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  responseData?: any;
  error?: Error;
  startTime: number;
  endTime?: number;
  duration?: number;
}

// Store for captured requests
let capturedRequests: RequestData[] = [];

// Listeners for request updates
const listeners: ((requests: RequestData[]) => void)[] = [];

// Flag to track if interception is already set up
let isInterceptionSetup = false;

/**
 * Set up request interception for fetch and XMLHttpRequest
 */
export function setupRequestInterception() {
  // Only set up once
  if (isInterceptionSetup) return;
  isInterceptionSetup = true;

  // Store original fetch
  const originalFetch = window.fetch;
  // Store original XMLHttpRequest open and send methods
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  // Override fetch
  window.fetch = async function(input, init) {
    const url = typeof input === 'string' ? input : input.url;
    const method = init?.method || (typeof input !== 'string' && input.method) || 'GET';
    
    // Create a unique ID for this request
    const requestId = `fetch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Record start time
    const startTime = performance.now();
    
    // Add request to captured requests
    const newRequest: RequestData = {
      id: requestId,
      url,
      method,
      startTime,
    };
    
    capturedRequests = [...capturedRequests, newRequest];
    notifyListeners();
    
    try {
      // Make the actual fetch request
      const response = await originalFetch.apply(this, [input, init]);
      
      // Clone the response to read its body
      const clonedResponse = response.clone();
      let responseData;
      
      try {
        // Try to parse as JSON
        responseData = await clonedResponse.json();
      } catch (e) {
        // If not JSON, get as text
        try {
          responseData = await clonedResponse.text();
        } catch (e) {
          responseData = 'Unable to parse response';
        }
      }
      
      // Record end time and calculate duration
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Update request in captured requests
      capturedRequests = capturedRequests.map(req => 
        req.id === requestId 
          ? { 
              ...req, 
              status: response.status, 
              statusText: response.statusText,
              responseData,
              endTime,
              duration
            } 
          : req
      );
      
      notifyListeners();
      return response;
    } catch (error) {
      // Record end time and calculate duration
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Update request in captured requests with error
      capturedRequests = capturedRequests.map(req => 
        req.id === requestId 
          ? { 
              ...req, 
              error,
              endTime,
              duration
            } 
          : req
      );
      
      notifyListeners();
      throw error;
    }
  };

  // Override XMLHttpRequest
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    // Store method and url on the XHR instance
    this._debugMethod = method;
    this._debugUrl = url;
    return originalXhrOpen.apply(this, [method, url, ...args]);
  };

  XMLHttpRequest.prototype.send = function(body) {
    // Create a unique ID for this request
    const requestId = `xhr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Record start time
    const startTime = performance.now();
    
    // Add request to captured requests
    const newRequest: RequestData = {
      id: requestId,
      url: this._debugUrl,
      method: this._debugMethod,
      startTime,
    };
    
    capturedRequests = [...capturedRequests, newRequest];
    notifyListeners();
    
    // Add event listeners to track response
    this.addEventListener('load', function() {
      // Record end time and calculate duration
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      let responseData;
      try {
        // Try to parse response as JSON
        responseData = JSON.parse(this.responseText);
      } catch (e) {
        // If not JSON, use as is
        responseData = this.responseText;
      }
      
      // Update request in captured requests
      capturedRequests = capturedRequests.map(req => 
        req.id === requestId 
          ? { 
              ...req, 
              status: this.status, 
              statusText: this.statusText,
              responseData,
              endTime,
              duration
            } 
          : req
      );
      
      notifyListeners();
    });
    
    this.addEventListener('error', function(error) {
      // Record end time and calculate duration
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Update request in captured requests with error
      capturedRequests = capturedRequests.map(req => 
        req.id === requestId 
          ? { 
              ...req, 
              error: new Error('Network Error'),
              endTime,
              duration
            } 
          : req
      );
      
      notifyListeners();
    });
    
    return originalXhrSend.apply(this, [body]);
  };
}

/**
 * Subscribe to request updates
 * @param listener Function to call when requests are updated
 * @returns Function to unsubscribe
 */
export function subscribeToRequests(listener: (requests: RequestData[]) => void) {
  listeners.push(listener);
  
  // Immediately notify with current requests
  listener([...capturedRequests]);
  
  // Return unsubscribe function
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
}

/**
 * Get all captured requests
 * @returns Array of captured requests
 */
export function getCapturedRequests() {
  return [...capturedRequests];
}

/**
 * Clear all captured requests
 */
export function clearCapturedRequests() {
  capturedRequests = [];
  notifyListeners();
}

/**
 * Notify all listeners of request updates
 */
function notifyListeners() {
  listeners.forEach(listener => {
    listener([...capturedRequests]);
  });
}

// Set up request interception immediately when this module is imported
// This ensures we capture requests as early as possible
if (typeof window !== 'undefined') {
  setupRequestInterception();
}